/**
 * Infrastructure Intelligence — Operations Dashboard
 *
 * Cross-step KPI summary for the full 8-step intelligence loop.
 * Aggregates live data from assets, anomalies, missions, and compliance actions
 * to give Infrastructure Managers a single situational-awareness view.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Building, AlertTriangle, Navigation, HardHat,
  TrendingUp, RefreshCw, AlertOctagon, MapPin, Scale,
} from 'lucide-react';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { KpiCard, StepPill, ActionBanner } from '../../components/common/OperationsDashboard';
import { useNavigate } from 'react-router-dom';
import { anomalyApi, satelliteBoundariesApi } from '../../services/api';
import api from '../../services/api';

interface DashboardData {
  assets:            number;
  pendingAnomalies:  number;
  criticalAnomalies: number;
  activeSurveys:     number;
  awaitingReview:    number;
  awaitingResponse:  number;
  encroachmentCount: number;
  constructionMissions: number;
}

export const InfraDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData]       = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anomalyStats, boundaryRes, missionRes, encroachmentRes] = await Promise.all([
        anomalyApi.getStats('infrastructure'),
        satelliteBoundariesApi.getAll(),
        api.get('/missions', { params: { limit: 200 } }),
        anomalyApi.getAnomalies({ vertical: 'infrastructure', status: 'pending' }),
      ]);

      const assets = (boundaryRes.data?.boundaries ?? []).filter(
        (b: any) => b.metadata?.vertical === 'infrastructure',
      ).length;

      const missions: any[] = missionRes.data?.missions ?? missionRes.data?.data?.missions ?? [];
      const infraMissions   = missions.filter((m) => m.metadata?.vertical === 'infrastructure');

      const activeSurveys = infraMissions.filter(
        (m) => m.mission_type === 'drone_survey' && (m.status === 'in_progress' || m.status === 'pending'),
      ).length;

      const awaitingReview = infraMissions.filter(
        (m) => m.metadata?.ai_analysis_status === 'complete' && !m.metadata?.step7_status,
      ).length;

      const awaitingResponse = infraMissions.filter(
        (m) =>
          (m.metadata?.step7_status === 'confirmed_critical' ||
           m.metadata?.step7_status === 'confirmed_monitor') &&
          !m.metadata?.step8_status,
      ).length;

      const encroachmentCount = (encroachmentRes.anomalies ?? []).filter(
        (a: any) => a.anomaly_type?.includes('encroachment') || a.anomaly_type?.includes('unauthorized'),
      ).length;

      const constructionMissions = infraMissions.filter(
        (m) => m.metadata?.earthwork_delta_m3 != null,
      ).length;

      setData({
        assets,
        pendingAnomalies:  anomalyStats?.byStatus?.pending    ?? 0,
        criticalAnomalies: anomalyStats?.bySeverity?.critical ?? 0,
        activeSurveys,
        awaitingReview,
        awaitingResponse,
        encroachmentCount,
        constructionMissions,
      });
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const actionRequired = (data?.pendingAnomalies ?? 0) + (data?.awaitingReview ?? 0) + (data?.awaitingResponse ?? 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Building className="w-7 h-7 text-amber-500" /> Infrastructure Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Live situational overview across the full 8-step infrastructure intelligence loop
          </p>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Updated {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
      {!loading && <ActionBanner count={actionRequired} />}

      {loading ? (
        <div className="flex justify-center py-14"><Spinner size="lg" /></div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            <KpiCard label="Assets Monitored"  value={data.assets}           icon={MapPin}        colour="bg-amber-100 dark:bg-amber-900/30 text-amber-600"   onClick={() => navigate('/infrastructure/boundaries')} />
            <KpiCard label="Pending Anomalies" value={data.pendingAnomalies} icon={AlertTriangle} colour={data.pendingAnomalies > 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'} onClick={() => navigate('/infrastructure/anomalies')} />
            <KpiCard label="Active Surveys"    value={data.activeSurveys}    icon={Navigation}    colour="bg-blue-100 dark:bg-blue-900/30 text-blue-600"      onClick={() => navigate('/infrastructure/flight-analysis')} />
            <KpiCard label="Response Required" value={data.awaitingResponse} icon={AlertOctagon}  colour={data.awaitingResponse > 0 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'} onClick={() => navigate('/infrastructure/response')} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Intelligence Loop — Action Queue</p>
              <StepPill step="1"   label="Asset Boundaries"        path="/infrastructure/boundaries" />
              <StepPill step="2"   label="Temporal Analysis"        path="/infrastructure/temporal" />
              <StepPill step="3–4" label="Anomaly Alerts"           count={data.pendingAnomalies}  urgent path="/infrastructure/anomalies" />
              <StepPill step="5"   label="Survey Mission Planner"   path="/infrastructure/survey-planner" />
              <StepPill step="6"   label="Flight Data & Analysis"   path="/infrastructure/flight-analysis" />
              <StepPill step="7"   label="Analysis Review"          count={data.awaitingReview}    urgent path="/infrastructure/analysis-review" />
              <StepPill step="8"   label="Response Planning"        count={data.awaitingResponse}  urgent path="/infrastructure/response" />
            </div>

            <div className="space-y-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Summary Metrics</p>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Critical Anomalies</p>
                  {data.criticalAnomalies > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Urgent</span>}
                </div>
                <p className={`text-3xl font-bold mt-1 ${data.criticalAnomalies > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{data.criticalAnomalies}</p>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">Awaiting Analysis Review (Step 7)</p>
                <p className={`text-3xl font-bold mt-1 ${data.awaitingReview > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>{data.awaitingReview}</p>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">Construction Surveys</p>
                <p className="text-3xl font-bold mt-1 text-blue-600 dark:text-blue-400">{data.constructionMissions}</p>
                {data.encroachmentCount > 0 && <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">{data.encroachmentCount} encroachment event{data.encroachmentCount !== 1 ? 's' : ''} pending</p>}
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quick Links</p>
                {[
                  { label: 'Construction Progress',   path: '/infrastructure/construction',  icon: HardHat },
                  { label: 'Encroachment Detection',  path: '/infrastructure/encroachment',  icon: AlertOctagon },
                  { label: 'Response Planning',        path: '/infrastructure/response',      icon: Scale },
                  { label: 'Analysis Review',          path: '/infrastructure/analysis-review', icon: TrendingUp },
                ].map(({ label, path, icon: Icon }) => (
                  <button key={path} onClick={() => navigate(path)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <Icon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    {label}
                    <span className="ml-auto text-gray-400">›</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InfraDashboardPage;

// Made with Bob
